/**
 * <NotFoundScreen> — full-bleed 404 fallback rendered by the router's
 * `defaultNotFoundComponent` when no route matches the current path.
 *
 * Wired centrally through `createAppRoot`'s `DefaultNotFoundComponent` option
 * (see `@linkhr/auth`), so every app gets a localized, RTL-safe 404 instead of
 * TanStack Router's bare "notFoundError" console warning.
 *
 * The CTA is a plain anchor (not a typed router `Link`) so this presentational
 * pattern stays router-agnostic — `homeHref` is the app's base path
 * (`import.meta.env.BASE_URL`), and a full navigation back to the shell is the
 * right behavior for an unmatched URL.
 */
import { forwardRef, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../primitives/Button";
import { cn } from "../../utils";
import { usePageTitle } from "../PageTitle/PageTitle";

export interface NotFoundScreenProps {
  /** Where the "Back to home" CTA points — typically the app's base path. */
  homeHref: string;
  /** Additional classes merged onto the root element. */
  className?: string;
}

function NotFoundScreenInner(
  { homeHref, className }: NotFoundScreenProps,
  ref: Ref<HTMLDivElement>,
): React.JSX.Element {
  const { t } = useTranslation("common");
  // Inside the shell the heading names the page from the top bar.
  const bar = usePageTitle({ title: t("not_found.title") });
  return (
    <div
      ref={ref}
      className={cn(
        "flex min-h-[60vh] flex-col items-center justify-center gap-md px-lg text-center",
        className,
      )}
    >
      <p className="text-display text-muted tabular-nums">404</p>
      {bar.inShell ? (
        bar.titleNode
      ) : (
        <h1 className="text-heading text-default">{t("not_found.title")}</h1>
      )}
      <p className="max-w-prose text-body text-muted">{t("not_found.body")}</p>
      <Button asChild variant="primary" size="md" className="mt-sm">
        <a href={homeHref}>{t("not_found.cta")}</a>
      </Button>
    </div>
  );
}

export const NotFoundScreen = forwardRef(NotFoundScreenInner);
NotFoundScreen.displayName = "NotFoundScreen";
